import { createSelector } from "@reduxjs/toolkit";
import _ from "lodash";

import type { AppState } from "state/store";

// For downloading, files AND flights can be selected. So we store a map of both
const getFileMap = (state: AppState) => state.fileSelector.selectedFileMap;
const getFlightMap = (state: AppState) => state.fileSelector.selectedFlightMap;
const getDeselectedFilesOfFlightMap = (state: AppState) =>
  state.fileSelector.deselectedFilesOfFlightMap;

export const getSelectedFilesList = createSelector(getFileMap, selectedFileMap =>
  _.values(selectedFileMap)
);
export const getSelectedFlightsList = createSelector(getFlightMap, selectedFlightMap =>
  _.values(selectedFlightMap)
);
// list of all files that were directly deselected from a selected flight
export const getExcludedFilesList = createSelector(
  getDeselectedFilesOfFlightMap,
  deselectedFilesOfFlightMap => _.values(deselectedFilesOfFlightMap).flat()
);

export const isFlightSelected = (state: AppState, flightId: string) =>
  flightId in state.fileSelector.selectedFlightMap;

export const isFileDirectlySelected = (state: AppState, fileUuid: string) =>
  fileUuid in state.fileSelector.selectedFileMap;

export const hasFilesDeselected = (state: AppState, flightId: string) =>
  flightId in state.fileSelector.deselectedFilesOfFlightMap &&
  state.fileSelector.deselectedFilesOfFlightMap[flightId].length > 0;

export const isFileDeselectedFromFlight = (state: AppState, fileUuid: string, flightId: string) =>
  flightId in state.fileSelector.deselectedFilesOfFlightMap &&
  state.fileSelector.deselectedFilesOfFlightMap[flightId].includes(fileUuid);

// These selectors determine whether files are selected for download, but doesn't care how they
// were selected (either directly or indirectly through a selected flight)
export const hasSelectedFiles = createSelector(
  getFileMap,
  getFlightMap,
  (selectedFileMap, selectedFlightMap) =>
    Object.keys(selectedFileMap).length > 0 || Object.keys(selectedFlightMap).length > 0
);
export const isFileSelected = (state: AppState, fileUuid: string, flightId: string) =>
  (isFileDirectlySelected(state, fileUuid) || flightId in state.fileSelector.selectedFlightMap) &&
  !isFileDeselectedFromFlight(state, fileUuid, flightId);

export const getSelectedFilesCount = createSelector(
  getFileMap,
  getFlightMap,
  getDeselectedFilesOfFlightMap,
  (selectedFileMap, selectedFlightMap, getDeselectedFilesOfFlightMap) =>
    Object.keys(selectedFileMap).length +
    Object.values(selectedFlightMap).reduce((acc, flight) => {
      return acc + flight.totalFileCount;
    }, 0) -
    Object.values(getDeselectedFilesOfFlightMap).reduce((acc, fileUuids) => {
      return acc + fileUuids.length;
    }, 0)
);
